// ─────────────────────────────────────────────────────────────────
// Glass-i — Process / Fabrication Master (Stage 6B.8A-3K).
//
// Spreadsheet-style grid (Excel-tight rows, sticky header, dynamic
// thickness columns, inline cell editing, bulk save). UI polished
// in Stage 6B.8A-3K.2++ to match the rest of the ERP master surfaces:
// header icon chip, single sticky toolbar card, inline search + dept
// filter, refined dirty-row indicator, zebra rows, status footer.
//
// The grid is the PRIMARY interface — no modal-first CRUD. Each row
// is editable inline; "Save Changes" sends a single bulk-save call.
// ─────────────────────────────────────────────────────────────────

import { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Button, InputNumber, Input, Select, Table, Tooltip, Space,
  Popconfirm, message, Spin
} from 'antd';
import {
  PlusOutlined, CloseOutlined, ArrowUpOutlined, ArrowDownOutlined,
  SaveOutlined, ReloadOutlined, SettingOutlined,
  SearchOutlined, FilterOutlined, ApartmentOutlined
} from '@ant-design/icons';

// Indian market — render the ₹ glyph instead of AntD's DollarOutlined.
// AntD ships no RupeeOutlined; using the Unicode character keeps the
// surrounding small-icon footprint without pulling in a new dep.
const RupeeIcon = () => (
  <span className="pm-rupee-icon" aria-hidden="true">₹</span>
);
import {
  processMasterApi,
  UOM_OPTIONS, DEPARTMENT_OPTIONS, CATEGORY_OPTIONS, DEFAULT_THICKNESSES
} from './processMasterApi';
import { listActiveThicknessesMm } from '../thicknesses/thicknessMasterApi';
// Stage 6B.8A-3K.3 — Production Department Master lookup (soft import).
// If the API call fails or returns empty, the page falls back to the
// hardcoded DEPARTMENT_OPTIONS list so existing rows keep editing.
import { loadDepartmentLookupsSafe } from '../productionDepartments/productionDepartmentsApi';
import {
  normalizeProcessRows, buildBulkSavePayload,
  validateProcessRows, nextClientRowId,
  COLUMN_WIDTHS, selectAllCategories, clearCategories,
  // Stage 6B.8A-3K.5 — assignment helpers
  normalizeDepartmentAssignments, defaultDepartmentAssignment,
  groupDepartmentOptions
} from './processMasterHelpers';
import ProcessDepartmentDrawer from './ProcessDepartmentDrawer';
import './ProcessMasterPage.css';

const NEW_ROW = () => ({
  _clientRowId:        nextClientRowId(),
  id:                  null,
  processName:         '',
  abbreviation:        '',
  uomCode:             'RFT',
  defaultRate:         0,
  departmentCode:      'POLISH',          // legacy fallback
  appliesToCategories: [],
  isActive:            true,
  thicknessRateMap:    {},
  // Stage 6B.8A-3K.5 — multi-department assignment list (replaces
  // single-select Dept). Empty by default; operator picks via cell.
  departmentAssignments: []
});

export default function ProcessMasterPage() {
  const [rows,        setRows]        = useState([]);
  const [thicknesses, setThicknesses] = useState(DEFAULT_THICKNESSES);
  const [dirty,       setDirty]       = useState(() => new Set());
  const [loading,     setLoading]     = useState(false);
  const [saving,      setSaving]      = useState(false);

  // Toolbar filters — operate on the in-memory rows only; bulk-save
  // still walks the original `rows` so a filtered view never causes
  // data loss.
  const [search,     setSearch]      = useState('');
  const [deptFilter, setDeptFilter]  = useState(undefined);

  // Stage 6B.8A-3K.3 — Department dropdown options.
  // Seeded with the legacy hardcoded list so the cell renders
  // immediately; replaced asynchronously by the lookup from the new
  // Production Department Master if it returns anything. Lookup
  // failure leaves the fallback in place — Process Master keeps working.
  const [departmentOptions, setDepartmentOptions] = useState(DEPARTMENT_OPTIONS);
  // Stage 6B.8A-3K.5 — full department lookup rows (id + name +
  // parentDepartmentId + workMode) used by the multi-select Dept cell
  // and the per-row Department Assignments drawer. Empty when the
  // Production Department Master is unavailable.
  const [departmentRows, setDepartmentRows] = useState([]);

  // Stage 6B.8A-3K.5 — pricing-mode drawer state.
  //   open: true|false
  //   processRow: the row object we're editing (assignments live here)
  const [deptDrawer, setDeptDrawer] = useState({ open: false, clientRowId: null });

  // ── Load ─────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listOut, activeMm] = await Promise.all([
        processMasterApi.list({ limit: 500, offset: 0 }),
        listActiveThicknessesMm()
      ]);
      const fresh = normalizeProcessRows((listOut && listOut.rows) || []);
      // Stage 6B.8A-3K.5 — also normalise departmentAssignments per row
      // so the cell + drawer can render without per-render coercion.
      const withClientIds = fresh.map((r) => ({
        ...r,
        _clientRowId: r.id || nextClientRowId(),
        departmentAssignments: normalizeDepartmentAssignments(
          r.departmentAssignments)
      }));
      setRows(withClientIds);
      setDirty(new Set());
      const sortedActive = [...new Set(activeMm)].sort((a, b) => a - b);
      setThicknesses(sortedActive.length > 0 ? sortedActive : DEFAULT_THICKNESSES);
    } catch (e) {
      message.error(e?.response?.data?.error?.message
        || e.message || 'Failed to load processes.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Stage 6B.8A-3K.3 / 3K.5 — pull department lookup from the master.
  //   • departmentOptions  = legacy single-select fallback (kept for
  //     legacy departmentCode column on existing process rows)
  //   • departmentRows     = full lookup (used by the new multi-select
  //     assignment cell + Department Rates drawer)
  // Prefer leaves (Manual / Machine subs + childless mains) so the
  // operator picks the most specific node available.
  // Failure path: hardcoded fallback stays in place.
  useEffect(() => {
    let mounted = true;
    (async () => {
      const lookup = await loadDepartmentLookupsSafe();
      if (!mounted) return;
      const leaves = lookup && Array.isArray(lookup.leaves) ? lookup.leaves : [];
      const all    = lookup && Array.isArray(lookup.rows)   ? lookup.rows   : [];
      if (all.length > 0) setDepartmentRows(all);
      if (leaves.length === 0) return;
      setDepartmentOptions(leaves.map((row) => ({
        value: row.departmentCode,
        label: row.departmentName
      })));
    })();
    return () => { mounted = false; };
  }, []);

  // ── Row helpers ──────────────────────────────────────────────
  const markDirty = (clientRowId) => setDirty((prev) => {
    const next = new Set(prev);
    next.add(clientRowId);
    return next;
  });
  const updateRow = (clientRowId, patch) => setRows((prev) => prev.map((r) =>
    r._clientRowId === clientRowId ? { ...r, ...patch } : r));
  const updateThicknessRate = (clientRowId, thickness, value) => {
    setRows((prev) => prev.map((r) => {
      if (r._clientRowId !== clientRowId) return r;
      const map = { ...(r.thicknessRateMap || {}) };
      if (value === '' || value === null || value === undefined) {
        delete map[thickness];
      } else {
        map[thickness] = Number(value);
      }
      return { ...r, thicknessRateMap: map };
    }));
    markDirty(clientRowId);
  };
  const addRow = () => {
    const fresh = NEW_ROW();
    setRows((prev) => [...prev, fresh]);
    markDirty(fresh._clientRowId);
  };
  const deleteRow = async (row) => {
    if (row.id) {
      try {
        await processMasterApi.remove(row.id);
        message.success(`Deleted "${row.processName}".`);
      } catch (e) {
        message.error(e?.response?.data?.error?.message || 'Delete failed.');
        return;
      }
    }
    setRows((prev) => prev.filter((r) => r._clientRowId !== row._clientRowId));
    setDirty((prev) => {
      const next = new Set(prev);
      next.delete(row._clientRowId);
      return next;
    });
  };
  const moveRow = (clientRowId, direction) => {
    setRows((prev) => {
      const idx = prev.findIndex((r) => r._clientRowId === clientRowId);
      if (idx < 0) return prev;
      const newIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (newIdx < 0 || newIdx >= prev.length) return prev;
      const next = prev.slice();
      [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
      return next;
    });
  };

  // ── Filtered view (display only — bulk-save uses full `rows`) ─
  const visibleRows = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    return rows.filter((r) => {
      if (deptFilter && r.departmentCode !== deptFilter) return false;
      if (!q) return true;
      const hay = `${r.processName || ''} ${r.abbreviation || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [rows, search, deptFilter]);

  // ── Bulk save ────────────────────────────────────────────────
  const dirtyRows = useMemo(() => rows.filter(
    (r) => dirty.has(r._clientRowId)
  ), [rows, dirty]);

  const onSave = async () => {
    if (dirtyRows.length === 0) return;
    const { ok, errors } = validateProcessRows(dirtyRows);
    if (!ok) { message.error(errors[0].message); return; }
    setSaving(true);
    try {
      const payload = buildBulkSavePayload(dirtyRows, thicknesses);
      const out = await processMasterApi.bulkSave(payload);
      message.success(`Saved ${out.total} process${out.total === 1 ? '' : 'es'}.`);
      load();
    } catch (e) {
      message.error(e?.response?.data?.error?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  // Stage 6B.8A-3K.5 — grouped department dropdown options for the
  // Dept multi-select cell. AntD `<Select options>` supports the
  // OptGroup shape directly: [{ label, options: [{ value, label }] }].
  const departmentGroupedOptions = useMemo(
    () => groupDepartmentOptions(departmentRows),
    [departmentRows]);

  // ── Columns ──────────────────────────────────────────────────
  const baseColumns = useMemo(() => ([
    // Sticky-left columns: applied via BOTH onCell (td) and
    // onHeaderCell (th). Don't use `column.className` here — AntD v5
    // doesn't propagate it to body cells reliably under
    // tableLayout="fixed", which leaves the body td un-classed and
    // the cells scroll while the header stays pinned.
    { title: '#', key: '__sno', width: COLUMN_WIDTHS.sno, align: 'center',
      onCell:       () => ({ className: 'pm-col-fixed-left-1' }),
      onHeaderCell: () => ({ className: 'pm-col-fixed-left-1' }),
      render: (_v, _r, i) => <span style={{ color: '#94a3b8' }}>{i + 1}</span> },
    { title: 'Process Name', dataIndex: 'processName',
      width: COLUMN_WIDTHS.processName,
      onCell:       () => ({ className: 'pm-col-fixed-left-2' }),
      onHeaderCell: () => ({ className: 'pm-col-fixed-left-2' }),
      render: (v, row) => (
        <Input className="pm-cell-input" size="small" variant="borderless"
          value={v}
          placeholder="e.g., Round Grinding"
          onChange={(e) => { updateRow(row._clientRowId, { processName: e.target.value });
            markDirty(row._clientRowId); }} />
      ) },
    { title: 'Abbr', dataIndex: 'abbreviation', width: COLUMN_WIDTHS.abbreviation,
      render: (v, row) => (
        <Input className="pm-cell-input" size="small" variant="borderless"
          value={v} placeholder="RG"
          onChange={(e) => { updateRow(row._clientRowId, { abbreviation: e.target.value });
            markDirty(row._clientRowId); }} />
      ) },
    { title: 'UOM', dataIndex: 'uomCode', width: COLUMN_WIDTHS.uom,
      render: (v, row) => (
        <Select size="small" value={v || 'PCS'} options={UOM_OPTIONS}
          style={{ width: '100%' }} variant="borderless"
          onChange={(picked) => { updateRow(row._clientRowId, { uomCode: picked });
            markDirty(row._clientRowId); }} />
      ) },
    { title: 'Dept', dataIndex: 'departmentAssignments',
      width: COLUMN_WIDTHS.department, className: 'pm-dept-cell',
      render: (_v, row) => {
        // The cell binds to the assignment LIST. Selecting a department
        // adds a default-pricing-mode assignment; deselecting removes it.
        // For pricing-mode overrides the operator clicks the ₹ icon to
        // open the Department Rates drawer.
        const selectedIds = (row.departmentAssignments || [])
          .filter((a) => a && a.isActive !== false)
          .map((a) => a.departmentId);
        return (
          <div className="pm-dept-cell-inner">
            <Select size="small" mode="multiple"
              value={selectedIds}
              style={{ width: '100%' }} variant="borderless"
              showSearch optionFilterProp="label"
              popupClassName="pm-dept-dropdown"
              maxTagCount={1}
              placeholder={<span className="pm-cat-all">No dept</span>}
              tagRender={({ label, value }) => {
                // Resolve a clear label from the lookup so the chip
                // shows the full department name even when the option
                // tree only carries the abbreviated label.
                const meta = departmentRows.find((d) => d.id === value);
                const fullName = (meta && meta.departmentName)
                  || (typeof label === 'string' ? label : value);
                const code = meta && meta.departmentCode;
                return (
                  <Tooltip title={code ? `${fullName} (${code})` : fullName}>
                    <span className="pm-cat-chip pm-dept-chip"
                          title={fullName}>
                      {fullName}
                    </span>
                  </Tooltip>
                );
              }}
              maxTagPlaceholder={(omit) => {
                const names = omit
                  .map((o) => {
                    const meta = departmentRows.find((d) => d.id === o.value);
                    return (meta && meta.departmentName)
                      || (typeof o.label === 'string' ? o.label : o.value);
                  })
                  .filter(Boolean);
                return (
                  <Tooltip title={names.join(', ')}>
                    <span className="pm-cat-overflow">+{omit.length}</span>
                  </Tooltip>
                );
              }}
              options={departmentGroupedOptions}
              onChange={(pickedIds) => {
                const existing = row.departmentAssignments || [];
                const byId = new Map(existing.map((a) => [a.departmentId, a]));
                // Build the new assignment list: preserve metadata for
                // existing rows, seed defaults for newly-picked rows.
                const next = pickedIds.map((id) => {
                  if (byId.has(id)) return { ...byId.get(id), isActive: true };
                  const meta = departmentRows.find((d) => d.id === id) || {};
                  return defaultDepartmentAssignment(id,
                    meta.departmentName || meta.departmentCode || '');
                });
                // If exactly one assignment, mark it default automatically.
                if (next.length === 1) next[0].isDefault = true;
                updateRow(row._clientRowId, { departmentAssignments: next });
                markDirty(row._clientRowId);
              }} />
            <Tooltip title="Department rates / pricing mode">
              <Button size="small" type="text"
                className="pm-dept-rates-btn"
                icon={<RupeeIcon />}
                onClick={() => setDeptDrawer({
                  open: true, clientRowId: row._clientRowId })} />
            </Tooltip>
          </div>
        );
      } },
    { title: 'Categories', dataIndex: 'appliesToCategories',
      width: COLUMN_WIDTHS.categories, className: 'pm-category-cell',
      render: (v, row) => (
        <Select size="small" mode="multiple" value={Array.isArray(v) ? v : []}
          options={CATEGORY_OPTIONS}
          style={{ width: '100%' }} variant="borderless"
          popupClassName="pm-cat-dropdown"
          maxTagCount={2} maxTagPlaceholder={(omit) => (
            <span className="pm-cat-overflow">+{omit.length}</span>
          )}
          allowClear={false}
          placeholder={<span className="pm-cat-all">ALL</span>}
          tagRender={({ label }) => (
            <span className="pm-cat-chip">{label}</span>
          )}
          onChange={(picked) => { updateRow(row._clientRowId, { appliesToCategories: picked });
            markDirty(row._clientRowId); }}
          dropdownRender={(menu) => (
            <>
              <div style={{ display: 'flex', gap: 4, padding: '4px 6px',
                            borderBottom: '1px solid #f0f0f0' }}>
                <Button size="small" type="link" style={{ padding: '0 6px', height: 22 }}
                  onClick={() => {
                    const all = selectAllCategories(
                      CATEGORY_OPTIONS.map((o) => o.value));
                    updateRow(row._clientRowId, { appliesToCategories: all });
                    markDirty(row._clientRowId);
                  }}>
                  Select All
                </Button>
                <Button size="small" type="link" style={{ padding: '0 6px', height: 22 }}
                  onClick={() => {
                    updateRow(row._clientRowId, { appliesToCategories: clearCategories() });
                    markDirty(row._clientRowId);
                  }}>
                  Clear (All)
                </Button>
              </div>
              {menu}
            </>
          )}
        />
      ) },
    // Default ₹ sits next to Categories so the operator-set rate
    // visually anchors the start of the per-thickness rate band.
    { title: 'Default ₹', dataIndex: 'defaultRate',
      width: COLUMN_WIDTHS.defaultRate, align: 'right',
      render: (v, row) => (
        <InputNumber size="small" min={0} precision={2}
          value={v == null ? 0 : v}
          variant="borderless" controls={false}
          style={{ width: '100%' }}
          onChange={(n) => { updateRow(row._clientRowId, { defaultRate: n == null ? 0 : Number(n) });
            markDirty(row._clientRowId); }} />
      ) }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ]), [departmentOptions, departmentGroupedOptions, departmentRows]);

  const thicknessColumns = useMemo(() => thicknesses.map((t) => ({
    title: `${t}mm`,
    key: `t-${t}`,
    width: COLUMN_WIDTHS.thicknessRate,
    align: 'right',
    className:    'pm-thickness-col',
    onHeaderCell: () => ({ className: 'pm-thickness-col' }),
    render: (_v, row) => {
      const val = row.thicknessRateMap && row.thicknessRateMap[t];
      return (
        <InputNumber size="small" min={0} precision={2}
          className="pm-rate-input"
          value={val == null ? '' : val}
          variant="borderless" controls={false}
          style={{ width: '100%' }}
          placeholder="—"
          onChange={(n) => updateThicknessRate(row._clientRowId, t, n)} />
      );
    }
  })), [thicknesses]);

  const actionColumn = {
    title: 'Actions', key: '__actions',
    width: COLUMN_WIDTHS.actions, align: 'center',
    // Sticky-right via onCell + onHeaderCell — same AntD-v5 reason as
    // the sticky-left columns above (column.className doesn't reliably
    // reach body td under tableLayout="fixed").
    onCell:       () => ({ className: 'pm-actions-cell pm-action-fixed' }),
    onHeaderCell: () => ({ className: 'pm-actions-cell pm-action-fixed' }),
    render: (_v, row, idx) => (
      <Space size={0}>
        <Tooltip title="Move up">
          <Button size="small" type="text" icon={<ArrowUpOutlined />}
            disabled={idx === 0}
            onClick={() => moveRow(row._clientRowId, 'up')} />
        </Tooltip>
        <Tooltip title="Move down">
          <Button size="small" type="text" icon={<ArrowDownOutlined />}
            disabled={idx === rows.length - 1}
            onClick={() => moveRow(row._clientRowId, 'down')} />
        </Tooltip>
        <Popconfirm title="Delete this process?" okType="danger" okText="Delete"
          onConfirm={() => deleteRow(row)}>
          <Tooltip title="Delete">
            <Button size="small" type="text" danger icon={<CloseOutlined />} />
          </Tooltip>
        </Popconfirm>
      </Space>
    )
  };

  const columns = [...baseColumns, ...thicknessColumns, actionColumn];

  // ── Render ───────────────────────────────────────────────────
  return (
    <div className="process-master-page">
      {/* Compact one-line page title + subtitle */}
      <div className="pm-headstrip">
        <div className="pm-headstrip__lead">
          <span className="pm-headstrip__icon"><ApartmentOutlined /></span>
          <div className="pm-headstrip__text">
            <span className="pm-headstrip__title">Process Master</span>
            <span className="pm-headstrip__subtitle">
              Rate per thickness — empty cell uses the default rate.
            </span>
          </div>
        </div>
      </div>

      {/* Toolbar — single clean row */}
      <div className="process-master-toolbar">
        <Input allowClear
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          placeholder="Search process or abbreviation…"
          value={search} onChange={(e) => setSearch(e.target.value)}
          style={{ width: 280 }} />
        <Select allowClear
          placeholder="All departments"
          suffixIcon={<FilterOutlined style={{ color: '#94a3b8' }} />}
          options={departmentOptions} value={deptFilter}
          onChange={(v) => setDeptFilter(v)}
          showSearch optionFilterProp="label"
          style={{ width: 150 }} />

        <div className="process-master-toolbar__divider" />

        <Tooltip title="Manage shared thickness list">
          <Link to="/erp/masters/thicknesses">
            <Button icon={<SettingOutlined />}>Manage Thickness</Button>
          </Link>
        </Tooltip>

        <div className="process-master-toolbar__divider" />

        <span className="pm-counter">
          <strong>{visibleRows.length}</strong>
          {visibleRows.length === rows.length ? '' : ` / ${rows.length}`} rows
        </span>
        {dirty.size > 0 ? (
          <span className="pm-counter pm-counter--warn">
            • <strong>{dirty.size}</strong> unsaved
          </span>
        ) : null}

        <div className="process-master-toolbar__spacer" />

        <Tooltip title="Reload from server">
          <Button icon={<ReloadOutlined />} onClick={load} />
        </Tooltip>
        <Button icon={<PlusOutlined />} onClick={addRow}>Add New</Button>
        <Button type="primary" icon={<SaveOutlined />}
          disabled={dirty.size === 0} loading={saving} onClick={onSave}>
          Save Changes
        </Button>
      </div>

      {/* Flat grid shell — drop AntD's `sticky` + `scroll` entirely so
          the table renders as a plain HTML table. CSS `position: sticky`
          on <thead> pins the header against `.pm-scroll-area`. This
          avoids AntD's split header/body containers (which were
          leaving the empty band between header and the first row). */}
      <div className="process-master-grid-shell">
        <Spin spinning={loading}>
          <div className="pm-scroll-area">
            <Table
              className="process-master-table"
              size="small"
              rowKey="_clientRowId"
              columns={columns}
              dataSource={visibleRows}
              pagination={false}
              tableLayout="fixed"
              rowClassName={(row) => dirty.has(row._clientRowId) ? 'pm-dirty-row' : ''}
              locale={{
                emptyText: (
                  <div className="pm-empty">
                    <div className="pm-empty__title">
                      {rows.length === 0
                        ? 'No processes yet'
                        : 'No processes match the current filter'}
                    </div>
                    <div className="pm-empty__sub">
                      {rows.length === 0
                        ? 'Click Add New to start building your process rate sheet.'
                        : 'Adjust the search or department filter to see more rows.'}
                    </div>
                    {rows.length === 0 ? (
                      <Button type="primary" size="small"
                        icon={<PlusOutlined />} onClick={addRow}>
                        Add First Process
                      </Button>
                    ) : null}
                  </div>
                )
              }}
            />
          </div>

          {/* Slim footer strip */}
          <div className="pm-footstrip">
            <span><strong>{visibleRows.length}</strong>
              {visibleRows.length === rows.length ? '' : ` / ${rows.length}`} rows
            </span>
            <span>·</span>
            <span><strong>{thicknesses.length}</strong> thickness
              {thicknesses.length === 1 ? '' : 'es'}
              {thicknesses.length > 0 && (
                <span style={{ color: '#94a3b8', marginLeft: 6 }}>
                  {thicknesses.slice(0, 8).map((t) => `${t}mm`).join(' · ')}
                  {thicknesses.length > 8 ? ` · +${thicknesses.length - 8}` : ''}
                </span>
              )}
            </span>
            <div style={{ flex: 1 }} />
            {dirty.size > 0 ? (
              <span style={{ color: '#92400e' }}>
                {dirty.size} unsaved change{dirty.size === 1 ? '' : 's'}
              </span>
            ) : (
              <span style={{ color: '#10b981' }}>All changes saved</span>
            )}
          </div>
        </Spin>
      </div>

      {/* Stage 6B.8A-3K.5 — pricing-mode editor for the selected row's
          assignments. Drawer owns local working state; on Apply we
          merge the result back into the parent row + mark dirty. */}
      <ProcessDepartmentDrawer
        open={deptDrawer.open}
        processName={(rows.find((r) => r._clientRowId === deptDrawer.clientRowId) || {}).processName || ''}
        defaultRate={(rows.find((r) => r._clientRowId === deptDrawer.clientRowId) || {}).defaultRate || 0}
        uomCode={(rows.find((r) => r._clientRowId === deptDrawer.clientRowId) || {}).uomCode || ''}
        assignments={(rows.find((r) => r._clientRowId === deptDrawer.clientRowId) || {}).departmentAssignments || []}
        departmentLookup={departmentRows}
        onClose={() => setDeptDrawer({ open: false, clientRowId: null })}
        onApply={(updated) => {
          updateRow(deptDrawer.clientRowId, { departmentAssignments: updated });
          markDirty(deptDrawer.clientRowId);
          setDeptDrawer({ open: false, clientRowId: null });
        }}
      />
    </div>
  );
}
